import { MenuItem, Typography, makeStyles } from '@material-ui/core'
import { Close, PowerSettingsNew } from '@material-ui/icons'
import React, { useState, useReducer, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  sanitizeFetchType,
  GET_MANY,
  GetManyResult,
  useTranslate,
} from 'react-admin'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { BarrierDto } from '../../../../core/dto/device/barrier/barrier.dto'
import { BarrierSelectStateDialog } from '../BarrierDialogs'
import { ToggleType } from '../../../../core/devices/barriers/barrier-set-boolean-status-button'
import {
  barrierSetPowerButtonInitialState,
  barrierSetPowerButtonReducer,
} from '../../../../core/devices/barriers/barrier-set-power-button.reducer'

const useStyles = makeStyles({
  icon: {
    position: 'absolute',
    left: 5,
    top: 5,
    fontSize: '24px !important',
  },
  crossIcon: {
    position: 'absolute',
    left: 0,
    top: 0,
    fontSize: 34,
  },
})

export const BarrierSetPowerBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  asRecurringJob,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<BarrierDto>()
  const notify = useNotify()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const classes = useStyles()

  const [{ mutateQuery }, dispatch] = useReducer(
    barrierSetPowerButtonReducer,
    barrierSetPowerButtonInitialState,
  )
  const [selectStateDialogOpen, setSelectStateDialogOpen] = useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [selectedState, setSelectedState] = useState(false)
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedBarriers, setSelectedBarriers] = useState<BarrierDto[]>([])

  const handleMutation = async (
    barrier: BarrierDto,
    recurringJobName?: string,
    cronExpression?: string,
    auditNote?: string,
  ) => {
    let error
    let query
    if (!asRecurringJob) {
      query = mutateQuery?.(barrier, auditNote)
    } else {
      query = mutateQuery?.(
        barrier,
        auditNote,
        recurringJobName,
        cronExpression,
      )
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: barrier.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: barrier.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleSetFreePass = async (
    recurringJobName?: string,
    cronExpression?: string,
    auditNote?: string,
  ) => {
    setBulkStateDialogOpen(true)
    const pendingRequests: Promise<any>[] = []
    const selectedBarriersResponse = (await mutate(
      {
        type: sanitizeFetchType(GET_MANY),
        resource: ResourceName.BARRIERS,
        payload: {
          ids: selectedIds,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => {
          notify(err?.message, 'error')
          setBulkStateDialogOpen(false)
        },
      },
    )) as GetManyResult<BarrierDto>
    if (!selectedBarriersResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedBarriersResponse.data.forEach((barrier: BarrierDto) => {
      pendingRequests.push(
        handleMutation(barrier, recurringJobName, cronExpression, auditNote),
      )
      selectedBarriers.push(barrier)
      bulkProgressStatus.push({
        id: barrier.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (selectedState) {
      dispatch({ type: ToggleType.OFF })
    } else {
      dispatch({ type: ToggleType.ON })
    }
  }, [selectedState])

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedBarriers([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.BARRIERS_SET_POWER_BUTTON) ||
        loading ||
        mutationLoading
      }
      onClick={() => setSelectStateDialogOpen(true)}
      button
    >
      <Typography>
        {translate(
          !asRecurringJob
            ? 'resources.barriers.actions.setPowerState'
            : 'resources.barriers.actions.setPowerState-recurring',
        )}
      </Typography>
      <BarrierSelectStateDialog
        open={selectStateDialogOpen}
        confirm={handleSetFreePass}
        method="powerState"
        close={() => setSelectStateDialogOpen(false)}
        state={selectedState}
        setState={setSelectedState}
        asRecurringJob={asRecurringJob}
        OnIcon={<PowerSettingsNew className={classes.icon} />}
        OffIcon={
          <>
            <PowerSettingsNew className={classes.icon} />
            <Close fontSize="large" className={classes.crossIcon} />
          </>
        }
        withAuditNote
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedBarriers}
        method="powerState"
        statuses={bulkProgressStatus}
        resource={ResourceName.BARRIERS}
      />
    </MenuItem>
  )
}
